import React, { useState } from 'react';
import Modal from 'react-bootstrap/lib/Modal';
import Button from 'react-bootstrap/lib/Button';

import { CSR_TYPES } from '../../../common/certTypes';

import Input from '../Input/Input';
import Switch from '../Switch/Switch';
import { httpPost } from '../../../../utils';

const IDS = {
    FQDN: 'fqdn',
    COUNTRY: 'country',
    STATE: 'state',
    LOCATION: 'location',
    ORGANIZATION: 'organization',
    ORGANIZATIONAL_UNIT: 'organizationalUnit',
    CUSTOM_SAN: 'customSan',
    ADD_DEFAULT_CUSTOM_SAM: 'addDefaultCustomSan'
};
const COUNTRY_PATTERN = /^[a-zA-Z]{2}$/;

const CertificateRequestPopup = ({ id, header, onSave, onCancel, certType }) => {
    const [serverFQDN, setServerFQDN] = useState('');
    const [country, setCountry] = useState('');
    const [state, setState] = useState('');
    const [location, setLocation] = useState('');
    const [organization, setOrganization] = useState('');
    const [organizationalUnit, setOrganizationalUnit] = useState('');
    const [customSAN, setCustomSAN] = useState('');
    const [addDefaultCustomSAN, setAddCustomSAN] = useState(true);
    const [error, setError] = useState('');

    const inputChanged = (inputId, value) => {
        if (inputId === IDS.FQDN) {
            setServerFQDN(value);
        } else if (inputId === IDS.COUNTRY) {
            setCountry(value);
        } else if (inputId === IDS.STATE) {
            setState(value);
        } else if (inputId === IDS.LOCATION) {
            setLocation(value);
        } else if (inputId === IDS.ORGANIZATION) {
            setOrganization(value);
        } else if (inputId === IDS.ORGANIZATIONAL_UNIT) {
            setOrganizationalUnit(value);
        } else if (inputId === IDS.CUSTOM_SAN) {
            setCustomSAN(value);
        } else if (inputId === IDS.ADD_DEFAULT_CUSTOM_SAM) {
            setAddCustomSAN(value);
        }
    };

    const saveHandler = async () => {
        if (!serverFQDN) {
            return setError('Server FQDN is required');
        }

        if (serverFQDN.includes('*')) {
            return setError(`${header} cannot be a wildcard certificate`);
        }

        if (country && !COUNTRY_PATTERN.test(country)) {
            return setError('Maximum two letters supported for the field Country');
        }

        try {
            setError('');
            const postData = {
                ServerFQDN: serverFQDN,
                Country: country,
                State: state,
                Location: location,
                Organization: organization,
                OU: organizationalUnit,
                CustomSAN: customSAN,
                AddDefaultCustomSAN: addDefaultCustomSAN,
                Type: certType
            };

            if (certType === CSR_TYPES.DOMAIN) {
                postData['DomainID'] = id;
            } else if (certType === CSR_TYPES.DOMAIN_LDAPS) {
                postData['DomainID'] = `LDAPS_${id}`;
            } else if (certType === CSR_TYPES.PUSH_SERVICES && id) {
                postData['PushServiceId'] = id;
            }

            const { data } = await httpPost('/Settings/GenerateCertificateRequest', postData);
            if (!data.result) {
                throw new Error('No data received from server');
            }

            onSave(data.CertificateRequest, serverFQDN, customSAN);
        } catch (err) {
            console.error(err);
            setError('Error Occurred');
        }
    };

    let fqdnTooltip = '';
    let customSanTooltip = '';
    if (certType === CSR_TYPES.TOWER) {
        fqdnTooltip = 'FQDN of the Silverfort Admin Console';
        customSanTooltip = 'Add any additional domain names, besides the server FQDN, to be trusted';
    } else if (certType === CSR_TYPES.PUSH_SERVICES) {
        fqdnTooltip = 'Enter the value: silverfort-push-services.FQDN';
        customSanTooltip = 'For offline MFA, enter the value: silverfort-fido2.FQDN, silverfort-tokens.FQDN';
    }

    return (
        <Modal
            id="settings-modal"
            dialogClassName="modal-md certificate-modal"
            show
            onHide={onCancel}
            backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>GENERATE CERTIFICATE REQUEST</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
                <div className="form-field">
                    <label className="form-label">Server FQDN:</label>
                    <Input
                        id={IDS.FQDN}
                        value={serverFQDN}
                        placeholder="Enter Server FQDN"
                        tooltip={fqdnTooltip}
                        changed={inputChanged}
                    />
                </div>
                <div className="form-field">
                    <label className="form-label">Country (Max. 2 letters):</label>
                    <Input id={IDS.COUNTRY} value={country} placeholder="Enter Country" changed={inputChanged} />
                </div>
                <div className="form-field">
                    <label className="form-label">State:</label>
                    <Input id={IDS.STATE} value={state} placeholder="Enter State" changed={inputChanged} />
                </div>
                <div className="form-field">
                    <label className="form-label">Location:</label>
                    <Input id={IDS.LOCATION} value={location} placeholder="Enter Location" changed={inputChanged} />
                </div>
                <div className="form-field">
                    <label className="form-label">Organization:</label>
                    <Input
                        id={IDS.ORGANIZATION}
                        value={organization}
                        placeholder="Enter Organization"
                        changed={inputChanged}
                    />
                </div>
                <div className="form-field">
                    <label className="form-label">Organizational Unit:</label>
                    <Input
                        id={IDS.ORGANIZATIONAL_UNIT}
                        value={organizationalUnit}
                        placeholder="Enter Organizational Unit"
                        changed={inputChanged}
                    />
                </div>
                <div className="form-field">
                    <label className="form-label">Custom SAN:</label>
                    <Input
                        id={IDS.CUSTOM_SAN}
                        type="textarea"
                        style={{ maxWidth: '350px' }}
                        rows={customSAN ? Math.min(customSAN.split('\n').length, 3) : 1}
                        value={customSAN}
                        placeholder="Enter each SAN in a new line"
                        tooltip={customSanTooltip}
                        changed={inputChanged}
                    />
                </div>
                {certType !== CSR_TYPES.DOMAIN && certType !== CSR_TYPES.DOMAIN_LDAPS && (
                    <div className="form-field">
                        <label className="form-label">Add default SAN without FQDN:</label>
                        <Switch id={IDS.ADD_DEFAULT_CUSTOM_SAM} value={addDefaultCustomSAN} changed={inputChanged} />
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                {error && <div className="footer-error">{error}</div>}
                <Button onClick={onCancel}>Cancel</Button>
                <Button bsStyle="success" onClick={saveHandler}>
                    Save
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default React.memo(CertificateRequestPopup);
